import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService, {
  FhirServiceEntry,
} from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import {
  useActiveAccountId,
  useActiveProjectCohortId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import moment from 'moment/moment';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Button } from '@lifeomic/chroma-react/components/Button';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { FileText } from '@lifeomic/chromicons';
import { OLDEST_POST_DATE, SUMMARY_CODE, TOPIC_CODE } from '../constants';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';

const fhirService = new FhirService('Communication');

const messages = defineMessages({
  noData: {
    id: 'ChatSummary.noData',
    defaultMessage: 'No data found',
  },
  generationDate: {
    id: 'ChatSummary.noData',
    defaultMessage:
      'AI-generated summary from chat between member and Zori dating back to {date}',
  },
  topicsHeader: {
    id: 'ChatSummary.topicsHeader',
    defaultMessage: "Member's Topics of Interest",
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  summaryDate: {
    color: theme.palette.text.hint,
  },
  topicsList: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    flex: '1 1 calc(33.333% - 16px)',
    gap: theme.spacing(0),
  },
  topicHeader: {
    paddingTop: theme.spacing(1),
    fontSize: theme.pxToRem(14),
    fontWeight: 'bold',
  },
  topicButton: {
    width: theme.spacing(15),
    '&.selected': {
      backgroundColor: theme.palette.graphite[50],
    },
    transition: 'background-color 0.2s ease-in-out',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    maxHeight: '100%',
  },
}));

export const ChatSummary = ({ patient }: { patient: fhir.Patient }) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const mixpanel = useMixpanel();

  const [results, setResults] = React.useState<
    FhirServiceEntry<'Communication'>[]
  >([]);
  const [fetchResults, fetchState] = useService(fhirService.search);

  const [selectedTopic, setselectedTopic] = React.useState<string>();

  const switchTopic = (topic?: string) => {
    setselectedTopic(topic === selectedTopic ? undefined : topic);
  };

  const activeAccountId = useActiveAccountId();
  const activeProjectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId() || undefined;

  React.useEffect(() => {
    (async () => {
      const queryResults = await fetchResults(
        activeAccountId,
        activeProjectId,
        {
          patient: patient.id,
          code: [SUMMARY_CODE, TOPIC_CODE].join(','),
          pageSize: 1000,
          cohortId,
        },
      );
      setResults(queryResults?.data.entry);
    })();
  }, [activeAccountId, cohortId, activeProjectId, fetchResults, patient]);

  const isFetching = fetchState?.isFetching;

  if (isFetching) {
    return (
      <Box align="center" fullHeight fullWidth justify="center">
        <DotLoader />
      </Box>
    );
  }

  const filterByCategoryCode =
    (code: string) => (_: FhirServiceEntry<'Communication'>) =>
      _.resource.category?.some(_ => _.coding?.some(_ => _.code === code));

  const summary = results.filter(filterByCategoryCode(SUMMARY_CODE))?.[0]
    ?.resource;

  if (!summary) {
    return (
      <Box align="center" justify="center" fullHeight fullWidth>
        <EmptyPageState
          illustration={FileText}
          message={formatMessage(messages.noData)}
        />
      </Box>
    );
  }

  const topics = results
    .filter(filterByCategoryCode(TOPIC_CODE))
    .map(_ => _.resource);

  const oldestDate = summary.extension?.find(
    _ => _.url === OLDEST_POST_DATE,
  )?.valueDateTime;

  return (
    <Box direction="column" paddingBottom={2} data-testid="chat-summary">
      <Text size="subbody">{summary.payload?.[0]?.contentString}</Text>
      <Text size="caption" className={classes.summaryDate}>
        {formatMessage(messages.generationDate, {
          date: oldestDate ? moment(oldestDate).format('lll') : 'unknown',
        })}
      </Text>
      <Text className={classes.topicHeader}>
        {formatMessage(messages.topicsHeader)}
      </Text>
      <Box className={classes.topicsList}>
        {topics.map((topic, index) => {
          const topicText = topic.category?.[0].text;

          return (
            <div key={index}>
              <Button
                variant="text"
                onClick={() => {
                  switchTopic(topic.id);
                  mixpanel.track('zora_topic_of_interest_opened', {
                    topic: topicText,
                  });
                }}
                className={`${classes.topicButton} ${
                  selectedTopic === topic.id ? 'selected' : ''
                }`}
              >
                {topicText}
              </Button>
            </div>
          );
        })}
      </Box>
      {topics
        .find(_ => _.id === selectedTopic)
        ?.payload?.map((payload, index) => (
          <Text key={index}>{payload.contentString}</Text>
        ))}
    </Box>
  );
};
